import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

export default function Footer() {
  const { getPage } = useContent();
  const brand = getPage("branding", {});
  const f = getPage("footer", {});
  const contact = getPage("contact", {});

  const companyName = brand.company_name || "Capstone";
  const logoSize = brand.logo_size || "medium";
  const logoSizeClass = { small: "h-8", medium: "h-10", large: "h-12", xlarge: "h-14", xxlarge: "h-16", xxxlarge: "h-[4.5rem]" }[logoSize] || "h-10";
  const offices = (contact.offices || []).slice(0, 2);

  return (
    <footer data-testid="main-footer" className="bg-navy-900 text-white">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <div className="max-w-3xl">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-6 font-sans">{f.cta_label || "Ready to Scale?"}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-white mb-6 leading-tight">{f.cta_headline || "Let's unlock your next chapter of growth."}</h2>
            <p className="text-white/60 font-sans text-base mb-10 max-w-xl leading-relaxed">{f.cta_text || ""}</p>
            <Link to="/contact" data-testid="footer-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">
              {f.cta_button || "Schedule a Consultation"} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={companyName} className={`${logoSizeClass} w-auto object-contain brightness-0 invert`} />
              ) : (
                <div className="w-8 h-8 bg-gold-500 rounded-sm flex items-center justify-center">
                  <span className="text-navy-900 font-serif font-semibold text-lg">{companyName[0]}</span>
                </div>
              )}
              <span className="font-serif text-lg font-semibold">{companyName}</span>
            </div>
            <p className="text-white/50 font-sans text-sm leading-relaxed">{f.description || ""}</p>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-6">Services</h4>
            <div className="space-y-3">
              {[{label:"Capital Raising",path:"/services/capital-raising"},{label:"Asset Management",path:"/services/asset-management"},{label:"Strategic Advisory",path:"/services/strategic-advisory"}].map(l=>(
                <Link key={l.path} to={l.path} className="block text-white/60 hover:text-white text-sm font-sans transition-colors">{l.label}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-6">Company</h4>
            <div className="space-y-3">
              {[{label:"About Us",path:"/about"},{label:"Leadership",path:"/team"},{label:"Insights",path:"/insights"},{label:"Case Studies",path:"/case-studies"},{label:"Contact",path:"/contact"}].map(l=>(
                <Link key={l.path} to={l.path} className="block text-white/60 hover:text-white text-sm font-sans transition-colors">{l.label}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-6">Offices</h4>
            <div className="space-y-4">
              {offices.map(o => (
                <div key={o.city} className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-white/80 text-sm font-sans font-medium">{o.city} {o.tag ? `(${o.tag})` : ""}</p>
                    <p className="text-white/50 text-xs font-sans">{o.address?.split(",").slice(0, 2).join(",")}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 mt-4">
                <Phone className="w-4 h-4 text-gold-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/60 text-sm font-sans">{brand.phone || ""}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gold-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/60 text-sm font-sans">{brand.email || ""}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs font-sans">&copy; {new Date().getFullYear()} {f.copyright || `${brand.company_full_name || companyName}. All rights reserved.`}</p>
          <div className="flex items-center gap-6">
            <span className="text-white/40 text-xs font-sans">Privacy Policy</span>
            <span className="text-white/40 text-xs font-sans">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
